import React from 'react'
import { Box, Button, Table, TableBody, TableHead, TableRow } from '@material-ui/core'
import MuiTableCell from '@material-ui/core/TableCell'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import BN from 'bn.js'
import clsx from 'clsx'
import { toWei } from 'web3-utils'

import { useAppState } from 'state'
import { LoanInfo } from 'models'
import { weiToNum, dateFromNow } from 'toolbelt'

import AaveIcon from 'components/Icons/Aave'

import { usePaladin } from 'hooks'

const useStyles = makeStyles({
  table: {
    border: '1px solid #A5A5A5',
    borderRadius: '4px',
  },
  thcell: {
    fontSize: '16px',
  },
  status: {
    fontSize: '12px',
    borderRadius: '4px',
    marginLeft: '1rem',
    boxSizing: 'border-box',
  },
  activeStatus: {
    color: '#E88C28',
    border: '1px solid #E88C28',
  },
  closedStatus: {
    color: ' #A5A5A5',
    border: '1px solid #A5A5A5',
  },
  miniButton: {
    fontSize: '14px',
    border: '0.5px solid #FFFFFF',
    width: '10%',
    display: 'inline',
    margin: 'auto 5%',
    cursor: 'pointer',
  },
})

const TableCell = withStyles({
  root: {
    borderBottom: '1px solid #A5A5A5',
  },
})(MuiTableCell)

const THCell = withStyles({
  root: {
    fontSize: '16px',
    borderBottom: '1px solid #A5A5A5',
  },
})(MuiTableCell)

interface LoanProps {
  loan: LoanInfo
  onAction: () => void
}

interface MiniButtonProps {
  text: string
  onClick: () => {}
}

const MiniButton = ({ text, onClick }: MiniButtonProps) => {
  const styles = useStyles()
  return (
    <Box className={styles.miniButton} onClick={onClick}>
      <span>{text}</span>
    </Box>
  )
}

interface LoanStatusProps {
  active: boolean
}

const LoanStatus = ({ active }: LoanStatusProps) => {
  const styles = useStyles()
  return (
    <span className={clsx(styles.status, active ? styles.activeStatus : styles.closedStatus)}>
      {active ? 'Active' : 'Closed'}
    </span>
  )
}

const Loan = ({ loan, onAction }: LoanProps) => {
  const { calculateTerminationDate, closeLoan, extendLoan } = usePaladin()
  const feesAmount = new BN(loan._feesAmount).sub(new BN(loan._feesUsed)).toString()
  const terminationDate = calculateTerminationDate(loan._amount, feesAmount)
  const expiryDate = dateFromNow(terminationDate || 0)
  return (
    <TableRow>
      <TableCell align="center">
        <AaveIcon />
      </TableCell>
      <TableCell align="left">
        Aave <LoanStatus active={!loan._closed} />
      </TableCell>
      <TableCell align="left">{expiryDate}</TableCell>
      <TableCell align="left">{weiToNum(loan._amount)}</TableCell>
      <TableCell align="left">{weiToNum(loan._feesAmount)}</TableCell>
      <TableCell align="left">
        {!loan._closed && (
          <>
            <MiniButton
              text="Extend"
              onClick={() =>
                extendLoan(loan._loanPool, toWei('0.02')).then(() => {
                  onAction()
                })
              }
            />
            <MiniButton
              text="Close"
              onClick={() =>
                closeLoan(loan._loanPool, loan._borrower).then(() => {
                  onAction()
                })
              }
            />
          </>
        )}
      </TableCell>
    </TableRow>
  )
}

const Loans = () => {
  const styles = useStyles()
  const { getLoans } = usePaladin()
  const [loans, setLoans] = React.useState<LoanInfo[]>([])
  const [refresh, setRefresh] = React.useState(false)

  const allLoans = async () => {
    const res: LoanInfo[] = await getLoans() // ? maybe skip await
    setLoans(res)
  }

  React.useEffect(() => {
    allLoans()
  }, [])

  React.useEffect(() => {
    if (refresh) {
      setRefresh(false)
      allLoans()
    }
  }, [refresh])

  return (
    <Box>
      <Table className={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <THCell align="left">Loans</THCell>
            <THCell align="left">Expiry Date</THCell>
            <THCell align="left">Voting Power</THCell>
            <THCell align="left">Fee</THCell>
            <THCell></THCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loans.map((loan) => (
            <Loan
              loan={loan}
              key={loan._loanPool + loan._amount + loan._feesAmount}
              onAction={() => {
                setRefresh(true)
              }}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default Loans
